import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { GearPal } from "../../types/Pal";
import {
  CreateUserAddressFormSchema,
  CreateUserAddressFormData,
} from "../../forms/CreateUserAddressFormSchema";
import {
  createGearpal,
  CreateGearPalData,
  updateGearPal,
  UpdateGearPalData,
} from "../../backend/api/gearlpal";
import {
  getCoordinates,
  GetCoordinatesData,
  GetCoordinatesResponse,
} from "../../backend/api/geo";
import UserAddressFormFields from "./UserAddressFormFields";

interface UserAddressFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: (updatedPal?: GearPal) => void;
  isEdit?: boolean;
  palToEdit?: GearPal | null;
}

const UserAddressForm: React.FC<UserAddressFormProps> = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  isEdit = false,
  palToEdit = null,
}) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  // Mutation per ottenere le coordinate
  const { mutate: getCoordinatesMutation } = useMutation({
    mutationFn: (data: GetCoordinatesData) =>
      getCoordinates(data, i18n.language),
  });

  // Mutation per creare GearPal (indirizzo)
  const { mutate: createGearPalMutation, isPending: isAddingGearPal } =
    useMutation({
      mutationFn: (data: CreateGearPalData) =>
        createGearpal(data, i18n.language),
      onSuccess: (data: any) => {
        queryClient.invalidateQueries({ queryKey: ["gearpals"] });
        queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });
        resetForm();
        const createdPal = data?.result as GearPal;
        onSubmitSuccess?.(createdPal);
        onClose();
        toast.success(
          t("forms.addressCreated", "Indirizzo creato con successo!")
        );
      },
      onError: (error: any) => {
        console.error("Errore durante la creazione dell'indirizzo:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          t("errors.unknownError", "Errore sconosciuto");
        toast.error(`${t("errors.error", "Errore")}: ${errorMessage}`);
      },
    });

  // Mutation per aggiornare GearPal (indirizzo)
  const { mutate: updateGearPalMutation, isPending: isUpdatingGearPal } =
    useMutation({
      mutationFn: ({ id, data }: { id: number; data: UpdateGearPalData }) =>
        updateGearPal(id, data, i18n.language),
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: ["gearpals"] });
        queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });
        resetForm();
        const updatedGearPal = response?.result as GearPal;
        onSubmitSuccess?.(updatedGearPal);
        onClose();
        toast.success(
          t("forms.addressUpdated", "Indirizzo aggiornato con successo!")
        );
      },
      onError: (error: any) => {
        console.error("Errore durante l'aggiornamento dell'indirizzo:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          t("errors.unknownError", "Errore sconosciuto");
        toast.error(`${t("errors.error", "Errore")}: ${errorMessage}`);
      },
    });

  // Form con validazione Zod
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, touchedFields, isSubmitted },
    reset: resetForm,
    setValue,
    watch,
    trigger,
  } = useForm<CreateUserAddressFormData>({
    resolver: zodResolver(CreateUserAddressFormSchema),
    mode: "onTouched",
    defaultValues: {
      name: "",
      zipcode: "",
      city: "",
      region: "",
      country: "Italy",
      coordinates: { lat: 0, lng: 0 },
      items: [],
    },
  });

  // Popola il form quando apri in modalità edit
  useEffect(() => {
    if (isEdit && palToEdit && isOpen) {
      const gearPal = palToEdit as GearPal;
      resetForm({
        name: gearPal.name,
        zipcode: gearPal.zipCode,
        city: gearPal.city,
        region: gearPal.region,
        country: gearPal.country || "Italy",
        coordinates: gearPal.coordinates || { lat: 0, lng: 0 },
        // Items non gestiti qui
        items: [],
      });
    } else if (!isEdit && isOpen) {
      // Reset form quando si apre per creare un nuovo indirizzo
      resetForm({
        name: "",
        zipcode: "",
        city: "",
        region: "",
        country: "Italy",
        coordinates: { lat: 0, lng: 0 },
        items: [],
      });
    }
  }, [isEdit, palToEdit, isOpen, resetForm]);

  // Reset del form quando viene chiuso
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  // Funzione per creare l'indirizzo
  const createAddress = async (formData: CreateUserAddressFormData) => {
    try {
      // Ottieni le coordinate
      const coordinatesData: GetCoordinatesData = {
        city: formData.city,
        zipCode: formData.zipcode,
        country: "Italy",
        palType: "GearPal",
      };

      const coordinates: GetCoordinatesResponse = await new Promise(
        (resolve, reject) => {
          getCoordinatesMutation(coordinatesData, {
            onSuccess: (data) => resolve(data),
            onError: (error: any) => {
              const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Errore sconosciuto";
              reject(new Error(errorMessage));
            },
          });
        }
      );

      // Prepara i dati per l'API come FormData
      const formDataToSend = new FormData();

      formDataToSend.append("name", formData.name);
      formDataToSend.append("zipcode", formData.zipcode);
      formDataToSend.append("city", formData.city);
      formDataToSend.append("region", formData.region);
      formDataToSend.append("country", formData.country);
      formDataToSend.append("coordinates", JSON.stringify(coordinates.data));

      // Invia items vuoti per la creazione indirizzo
      formDataToSend.append("items", JSON.stringify([]));

      // Esegui la mutation con FormData
      createGearPalMutation(formDataToSend as any);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t("errors.unknownError", "Errore sconosciuto");
      toast.error(t("errors.error", "Errore") + ": " + message);
    }
  };

  // Funzione per aggiornare l'indirizzo
  const updateAddress = async (formData: CreateUserAddressFormData) => {
    if (!palToEdit?.id) return;

    try {
      const gearPal = palToEdit as GearPal;
      let coordinates = gearPal.coordinates;

      // Controlla se i campi geografici sono cambiati
      const locationChanged =
        formData.city !== gearPal.city || formData.zipcode !== gearPal.zipCode;

      // Se la posizione è cambiata, ricalcola le coordinate
      if (locationChanged) {
        const coordinatesData: GetCoordinatesData = {
          city: formData.city,
          zipCode: formData.zipcode,
          country: "Italy",
          palType: "GearPal",
        };

        const coordinatesResponse: GetCoordinatesResponse = await new Promise(
          (resolve, reject) => {
            getCoordinatesMutation(coordinatesData, {
              onSuccess: (data) => resolve(data),
              onError: (error: any) => {
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.message ||
                  "Errore sconosciuto";
                reject(new Error(errorMessage));
              },
            });
          }
        );

        coordinates = coordinatesResponse.data;
        setValue("coordinates", coordinates);
      }

      // Prepara i dati per l'API come FormData
      const formDataToSend = new FormData();

      formDataToSend.append("name", formData.name);
      formDataToSend.append("zipcode", formData.zipcode);
      formDataToSend.append("city", formData.city);
      formDataToSend.append("region", formData.region);
      formDataToSend.append("country", formData.country);
      formDataToSend.append("coordinates", JSON.stringify(coordinates));

      // Se ci sono items, li rimandiamo tutti così come sono, PRESI DAL PAL ORIGINALE (non dal form)
      if (gearPal.items && gearPal.items.length > 0) {
        const itemsToSend = gearPal.items.map((item) => ({
          name: item.name,
          categoryId: item.categoryId.toString(), // Assicurati che sia stringa per l'API se necessario, o number se l'interfaccia lo richiede.
          // Nota: GearItem ha categoryId number, ma UpdateGearPalData items ha categoryId string.
          externalLink: item.externalLink || "",
        }));
        formDataToSend.append("items", JSON.stringify(itemsToSend));
      }

      // Esegui la mutation con FormData
      updateGearPalMutation({ id: palToEdit.id, data: formDataToSend as any });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t("errors.unknownError", "Errore sconosciuto");
      toast.error(t("errors.error", "Errore") + ": " + message);
    }
  };

  // Handler per il submit
  const handleSubmitForm = async (formData: CreateUserAddressFormData) => {
    if (isEdit && palToEdit?.id) {
      await updateAddress(formData);
    } else {
      await createAddress(formData);
    }
  };

  // Handler per chiudere il modal cliccando fuori dal pannello
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#1F1235] border border-white/10 shadow-[0_30px_60px_rgba(3,0,28,0.55)]">
        <div className="flex justify-between items-start px-6 pt-6">
          <div>
            <h2 className="text-2xl font-semibold text-secondary mt-2">
              {isEdit
                ? t("forms.editAddressTitle", "Modifica il tuo indirizzo")
                : t("forms.addAddressTitle", "Aggiungi un nuovo indirizzo")}
            </h2>
            <p className="text-white/70 text-sm mt-1">
              {t(
                "forms.addressModalSubtitle",
                "Inserisci i dettagli del tuo indirizzo per aiutare altri utenti a trovarti."
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white"
            type="button"
          >
            ✕
          </button>
        </div>

        <form
          className="px-6 pb-6 pt-4"
          onSubmit={handleSubmit(handleSubmitForm)}
        >
          <UserAddressFormFields
            register={register}
            errors={errors}
            touchedFields={touchedFields}
            isSubmitted={isSubmitted}
            watch={watch}
            setValue={setValue}
            trigger={trigger}
            isValid={isValid}
            isAdding={isAddingGearPal}
            isUpdating={isUpdatingGearPal}
            isEdit={isEdit}
            palToEdit={palToEdit}
            onClose={onClose}
          />
        </form>
      </div>
    </div>
  );
};

export default UserAddressForm;
